import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { ErrorCode, useDropzone } from "react-dropzone";
import { useUploadPhotoMutation } from "@store/photoApi";
import FileUploadComponent from "@components/FileUpload/FileUpload";
import { Patient } from "@models/patient";
import TextIconButton from "@components/TextIconButton";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import { Box, Divider, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { decUploadCount, incUploadCount } from "@store/photoUploadSlice";
import { ImageResizeService } from "@service/image-resize.service";
import { HeicConverterService } from "@service/heic-converter.service";
import { useGetPlanSubscriptionDetailsQuery } from "@store/planApi";
import { RootState } from "@store/store";
import { setMessage } from "@store/snackbarSlice";
import { photosUploadMaxFiles } from "@utils/subscription.util";

const MAX_IMAGE_SIZE = 1920;

const FileDropZone = styled.div<{ active: boolean }>`
  width: 100%;
  min-height: 35vh;
  border-radius: 4px;
  border-width: 1px;
  border-color: #bdc1caff;
  border-style: dashed;
  position: relative;
`;

const FileInput = styled.input`
  display: none;
`;

const DropZoneContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #171a1fff;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
`;

const FilesContainer = styled.div`
  padding: 16px 24px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(21%, 1fr));
  grid-column-gap: 16px;
  grid-row-gap: 8px;
`;

export type FileUploadStatus = "Done" | "Uploading" | "Error";

interface FileUpload {
  _id: string;
  file: File | string;
  status: FileUploadStatus | null;
  progress?: number;
}

const PicturesUpload: FC<{
  patient: Patient;
  buttons: (props: {
    save: () => Promise<void>;
    isDone: boolean;
    isSubmitting: boolean;
  }) => ReactElement;
}> = ({ patient, buttons }) => {
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: subscription } = useGetPlanSubscriptionDetailsQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );
  const maxFiles = photosUploadMaxFiles(subscription);
  const photosPlanLimit = subscription?.plan.limits.photos;
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileUpload[]>([]);
  const dispatch = useDispatch();
  const [upload] = useUploadPhotoMutation();
  const isSubmitting = useMemo(() => {
    if (!files?.length) {
      return false;
    }
    return files.some(({ status }) => status === "Uploading");
  }, [files]);

  const isDone = useMemo(() => {
    if (!files.length) {
      return true;
    }
    return files.every(
      ({ status }) => status && ["Done", "Error"].includes(status),
    );
  }, [files]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    acceptedFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        if (
          [".heic", ".heif"].some((ext) =>
            file.name?.toLowerCase().endsWith(ext),
          )
        ) {
          return HeicConverterService.instance.convert(file);
        }
        return file;
      }),
    );
    setFiles((files) => [
      ...files,
      ...acceptedFiles.map((file) => ({
        _id: crypto.randomUUID(),
        file,
        status: null,
      })),
    ]);
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      maxFiles,
      accept: {
        "image/png": [".png"],
        "image/jpeg": [".jpg", ".jpeg"],
        "image/heic": [".heif"],
        "image/heif": [".heif"],
      },
    });

  useEffect(() => {
    if (!fileRejections?.length) {
      return;
    }
    if (
      fileRejections.some((file) =>
        file.errors.find((error) => error.code === ErrorCode.TooManyFiles),
      )
    ) {
      dispatch(
        setMessage({
          content: t(`errors.maxFiles`, {
            maxFiles: maxFiles || photosPlanLimit,
          }),
          severity: "error",
        }),
      );
    }
  }, [fileRejections, maxFiles, photosPlanLimit, dispatch, t]);

  const updateFileUploadStatus = (
    fileUpload: FileUpload,
    status: FileUploadStatus,
  ) => {
    setFiles((files) => {
      const index = files.findIndex((file) => file._id === fileUpload._id);
      return [
        ...files.slice(0, index),
        {
          ...fileUpload,
          status,
        },
        ...files.slice(index + 1),
      ];
    });
  };

  const uploadFile = async (fileUpload: FileUpload) => {
    dispatch(incUploadCount());
    try {
      const resizedFile = await ImageResizeService.instance.resize(
        fileUpload.file as File,
        MAX_IMAGE_SIZE,
      );

      updateFileUploadStatus(fileUpload, "Uploading");
      const { error } = await upload({
        file: resizedFile,
        fileName: resizedFile.name,
        patientId: patient!.id,
      });
      if (error && (error as any).status === 429) {
        dispatch(
          setMessage({
            content: t(`errors.maxFiles`, {
              maxFiles: maxFiles || photosPlanLimit,
            }),
            severity: "error",
          }),
        );
      }
      updateFileUploadStatus(fileUpload, error ? "Error" : "Done");
    } catch (e) {
      updateFileUploadStatus(fileUpload, "Error");
    } finally {
      dispatch(decUploadCount());
    }
  };

  const save = async () => {
    if (!files.length) {
      return;
    }

    await Promise.all(
      files
        .filter(
          ({ status, file }) => status === null && typeof file !== "string",
        )
        .map((file) => uploadFile(file)),
    );
  };

  const remove = (fileUpload: FileUpload) => {
    setFiles((files) => files.filter((file) => file._id !== fileUpload._id));
  };

  return (
    <>
      <FileDropZone {...getRootProps()} active={isDragActive}>
        <FileInput
          {...getInputProps()}
          accept="image/png, image/jpeg, image/jpg, image/heic, image/heif"
        />
        {files?.length ? (
          <FilesContainer>
            {files.map((fileUpload) => {
              const { _id, file, status, progress } = fileUpload;
              return (
                <FileUploadComponent
                  key={_id}
                  file={file}
                  status={status}
                  progress={progress}
                  onRemove={!status ? () => remove(fileUpload) : undefined}
                />
              );
            })}
          </FilesContainer>
        ) : (
          <DropZoneContent>
            <Typography>{t("picturesUpload.dropZoneMessage")}</Typography>
            <Box
              display="flex"
              gap="16px"
              alignItems="center"
              justifyContent="center"
            >
              <Divider sx={{ width: "27px" }} />
              <Typography textTransform="uppercase">{t("or")}</Typography>
              <Divider sx={{ width: "27px" }} />
            </Box>
            <TextIconButton
              variant="outlined"
              text={t("picturesUpload.selectFilesButton")}
              icon={BackupOutlinedIcon}
              iconLeft={true}
            />
          </DropZoneContent>
        )}
      </FileDropZone>

      {buttons({ save, isDone, isSubmitting })}
    </>
  );
};

export default PicturesUpload;
